import { AxiosResponse } from 'axios';
import { ICreateRoleResponse, IDeleteRoleResponse, IGetPermissionsResponse, IUpdateRoleResponse, SaveRolePermissionsRequest } from 'models/http/roles';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { prepareFiltersOfSelection, serverAxios } from 'utils/http';
import { IGetHistoryResponse } from 'models/http/history';
import { IGetSimpleListFilters, IGetSimpleListResponse } from 'models/http/common';

class RolesHttp {
  private static route = '/roles';

  public async getPermissions(abortController?: AbortController): Promise<IGetPermissionsResponse> {
    return serverAxios
      .get(`${RolesHttp.route}/permissions`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetPermissionsResponse>) => {
        return response.data;
      });
  }

  public async getSimple(_?: IGetSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    return serverAxios
      .get(`${RolesHttp.route}/simple`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async getSimpleGroupedBySite(abortController?: AbortController): Promise<{ [siteId: string]: IGetSimpleListResponse }> {
    return serverAxios
      .get(`${RolesHttp.route}/simple/groupedBySite`, { signal: abortController?.signal, timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<Promise<{ [siteId: string]: IGetSimpleListResponse }>>) => {
        return response.data;
      });
  }

  public async create(name: string, description: string): Promise<number> {
    return serverAxios.post(RolesHttp.route, { name, description }).then((response: AxiosResponse<ICreateRoleResponse>) => {
      return response.data.id;
    });
  }

  public async update(rolePermissions: SaveRolePermissionsRequest, abortController?: AbortController): Promise<void> {
    return serverAxios
      .patch(RolesHttp.route, { roles: rolePermissions }, { signal: abortController?.signal, timeout: HttpTimeoutPriority.Low })
      .then(() => {
        return;
      });
  }

  public async reset(roleId: number, abortController?: AbortController): Promise<IUpdateRoleResponse> {
    return serverAxios
      .put(`${RolesHttp.route}/${roleId}/reset`, { signal: abortController?.signal, timeout: HttpTimeoutPriority.Low })
      .then((response: AxiosResponse<IUpdateRoleResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[]): Promise<IDeleteRoleResponse> {
    return serverAxios
      .delete(RolesHttp.route, {
        data: prepareFiltersOfSelection(ids),
      })
      .then((response: AxiosResponse<IDeleteRoleResponse>) => {
        return response.data;
      });
  }

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`${RolesHttp.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }
}

export default RolesHttp;
